// ============================================================================
// Python du lycée — lecture du texte (refonte lot 5.7, moteur v28).
// ============================================================================
// Découpe un programme en jetons, avec l'indentation (INDENT / DEDENT) comme en
// Python : une ligne logique se termine par NEWLINE, sauf à l'intérieur de
// parenthèses, crochets ou accolades. Les commentaires (#) et les lignes vides
// sont ignorés. Pur, sans dépendance.
// ============================================================================

import { PyError } from './errors';

export type TokKind = 'name' | 'num' | 'str' | 'op' | 'newline' | 'indent' | 'dedent' | 'eof';

export interface Tok {
  t: TokKind;
  v: string;
  /** ligne (à partir de 1) */
  line: number;
}

const OPS = [
  '**=',
  '//=',
  '**',
  '//',
  '==',
  '!=',
  '<=',
  '>=',
  '+=',
  '-=',
  '*=',
  '/=',
  '%=',
  '->',
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
  '=',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  ',',
  ':',
  '.',
  ';',
];

/** au-delà, le programme n'est pas un exercice du lycée */
export const MAX_SOURCE_LENGTH = 4000;

export function tokenize(src: string): Tok[] {
  if (src.length > MAX_SOURCE_LENGTH) throw new PyError('syntax', 'Programme trop long.', 1);
  const out: Tok[] = [];
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const indents = [0];
  let depth = 0;
  for (let li = 0; li < lines.length; li++) {
    const raw = lines[li]!;
    const lineNo = li + 1;
    let i = 0;
    if (depth === 0) {
      // indentation de la ligne
      let col = 0;
      while (i < raw.length && (raw[i] === ' ' || raw[i] === '\t')) {
        col += raw[i] === '\t' ? 4 : 1;
        i++;
      }
      const rest = raw.slice(i);
      if (!rest.trim() || rest.trimStart().startsWith('#')) continue;
      const top = indents[indents.length - 1]!;
      if (col > top) {
        indents.push(col);
        out.push({ t: 'indent', v: '', line: lineNo });
      } else if (col < top) {
        while (indents[indents.length - 1]! > col) {
          indents.pop();
          out.push({ t: 'dedent', v: '', line: lineNo });
        }
        if (indents[indents.length - 1] !== col)
          throw new PyError('syntax', 'Indentation incohérente.', lineNo);
      }
    }
    while (i < raw.length) {
      const c = raw[i]!;
      if (c === ' ' || c === '\t') {
        i++;
        continue;
      }
      if (c === '#') break;
      if (c === '\\' && i === raw.length - 1) {
        i++;
        continue;
      }
      if (/[A-Za-z_À-ɏ]/.test(c)) {
        let j = i + 1;
        while (j < raw.length && /[A-Za-z0-9_À-ɏ]/.test(raw[j]!)) j++;
        out.push({ t: 'name', v: raw.slice(i, j), line: lineNo });
        i = j;
        continue;
      }
      if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(raw[i + 1] ?? ''))) {
        const m = /^(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?/.exec(raw.slice(i))!;
        if (/[A-Za-z_]/.test(raw[i + m[0].length] ?? ''))
          throw new PyError('syntax', 'Nombre mal écrit.', lineNo);
        out.push({ t: 'num', v: m[0], line: lineNo });
        i += m[0].length;
        continue;
      }
      if (c === '"' || c === "'") {
        let j = i + 1;
        let s = '';
        while (j < raw.length && raw[j] !== c) {
          if (raw[j] === '\\' && j + 1 < raw.length) {
            const e = raw[j + 1]!;
            s += e === 'n' ? '\n' : e === 't' ? '\t' : e;
            j += 2;
          } else s += raw[j++];
        }
        if (j >= raw.length)
          throw new PyError('syntax', 'Chaîne de caractères non fermée.', lineNo);
        out.push({ t: 'str', v: s, line: lineNo });
        i = j + 1;
        continue;
      }
      const op = OPS.find((o) => raw.startsWith(o, i));
      if (!op) throw new PyError('syntax', `Caractère inattendu « ${c} ».`, lineNo);
      if (op === '(' || op === '[' || op === '{') depth++;
      if (op === ')' || op === ']' || op === '}') depth = Math.max(0, depth - 1);
      out.push({ t: 'op', v: op, line: lineNo });
      i += op.length;
    }
    if (depth === 0 && out.length && out[out.length - 1]!.t !== 'newline')
      out.push({ t: 'newline', v: '', line: lineNo });
  }
  if (depth > 0) throw new PyError('syntax', 'Parenthèse non fermée.', lines.length);
  const last = lines.length;
  if (out.length && out[out.length - 1]!.t !== 'newline')
    out.push({ t: 'newline', v: '', line: last });
  while (indents.length > 1) {
    indents.pop();
    out.push({ t: 'dedent', v: '', line: last });
  }
  out.push({ t: 'eof', v: '', line: last });
  return out;
}
